import { Link, createFileRoute } from "@tanstack/react-router";
import { PageLayout } from "../../components/PageLayout";
import { Logo } from "../../components/ui/Logo";
import { useAuth } from "../../contexts/AuthContext";

export const Route = createFileRoute("/_layout/404")({
    component: NotFoundPage,
    head: () => ({
        meta: [
            { name: "title", content: "Page Not Found - StoryDoodle" },
            {
                name: "description",
                content:
                    "Oops! This page wandered off the story path. Return to StoryDoodle to create interactive AI-powered stories with your drawings.",
            },
            { name: "robots", content: "noindex, nofollow" },
            { property: "og:title", content: "Page Not Found - StoryDoodle" },
            {
                property: "og:description",
                content:
                    "Oops! This page wandered off the story path. Return to StoryDoodle to create interactive AI-powered stories with your drawings.",
            },
            { property: "og:url", content: "https://storydoodle.app/404" },
        ],
        links: [{ rel: "canonical", href: "https://storydoodle.app/404" }],
    }),
});

function NotFoundPage() {
    const { user } = useAuth();

    return (
        <PageLayout title="Page Not Found" showHeader={false}>
            <div className="flex min-h-[80vh] flex-col items-center justify-center px-4 text-center">
                {/* Logo */}
                <div className="mb-8">
                    <Logo />
                </div>

                {/* 404 Message */}
                <div className="mb-8 space-y-4">
                    <h1 className="text-6xl font-bold text-primary">404</h1>
                    <h2 className="text-2xl font-semibold text-foreground">Oops! Page Not Found</h2>
                    <p className="mx-auto max-w-md text-muted-foreground">
                        Looks like this page wandered off the story path! Let's get you back to creating amazing
                        stories.
                    </p>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-4 sm:flex-row">
                    <Link
                        to="/"
                        className="rounded-lg bg-primary px-6 py-3 font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                    >
                        Back to Home
                    </Link>
                    {user ? (
                        <>
                            <Link
                                to="/story-mode"
                                className="rounded-lg border border-border bg-background px-6 py-3 font-medium text-foreground transition-colors hover:bg-muted"
                            >
                                Start a Story
                            </Link>
                            <Link
                                to="/my-stories"
                                className="rounded-lg border border-border bg-background px-6 py-3 font-medium text-foreground transition-colors hover:bg-muted"
                            >
                                My Stories
                            </Link>
                        </>
                    ) : (
                        <>
                            <Link
                                to="/sign-in"
                                className="rounded-lg border border-border bg-background px-6 py-3 font-medium text-foreground transition-colors hover:bg-muted"
                            >
                                Sign In
                            </Link>
                            <Link
                                to="/sign-up"
                                className="rounded-lg border border-border bg-background px-6 py-3 font-medium text-foreground transition-colors hover:bg-muted"
                            >
                                Sign Up
                            </Link>
                        </>
                    )}
                </div>

                {/* Fun Message */}
                <div className="mt-12 rounded-lg bg-muted/50 p-4">
                    <p className="text-sm text-muted-foreground">
                        💡 <strong>Fun fact:</strong> Every great story has unexpected twists... even 404 errors!
                    </p>
                </div>
            </div>
        </PageLayout>
    );
}
